import { RefObject, useEffect, useRef } from 'react';

const ENHANCED_ATTR = 'data-a11y-enhanced';
const NATIVELY_FOCUSABLE = ['button', 'a', 'input', 'select', 'textarea'];

interface Options {
	countrySelectLabel?: string;
}
const ITEM_SELECTOR = [
	'[id^="account-"]:not(#account-selection-step)',
	'[class*="bank-row-container"]:not([class*="bank-row-skeleton"])',
	'[class*="show-more-button"]',
	'#bank-select'
].join(', ');

const handleKeyDown = (event: KeyboardEvent): void => {
	if (event.key !== 'Enter' && event.key !== ' ') return;
	event.preventDefault();
	(event.currentTarget as HTMLElement).click();
};

const enhance = (root: HTMLElement, options: Options): void => {
	const items = root.querySelectorAll<HTMLElement>(ITEM_SELECTOR);
	items.forEach((element) => {
		if (element.getAttribute(ENHANCED_ATTR)) return;
		if (NATIVELY_FOCUSABLE.includes(element.tagName.toLowerCase())) return;

		element.setAttribute(ENHANCED_ATTR, 'true');
		if (!element.hasAttribute('tabindex')) element.setAttribute('tabindex', '0');
		if (!element.hasAttribute('role')) element.setAttribute('role', 'button');
		if (element.id === 'bank-select' && options.countrySelectLabel && !element.getAttribute('aria-label')) {
			element.setAttribute('aria-label', options.countrySelectLabel);
		}
		element.addEventListener('keydown', handleKeyDown);
	});
};

export const useBankListKeyboardAccessibility = <T extends HTMLElement>(options: Options = {}): RefObject<T> => {
	const containerRef = useRef<T>(null);
	const optionsRef = useRef(options);
	optionsRef.current = options;
	const searchFocused = useRef<boolean>(false);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const run = () => {
			enhance(container, optionsRef.current);

			if (!searchFocused.current) {
				const search = container.querySelector<HTMLInputElement>('#banks-search-input');
				if (search) {
					search.focus({ preventScroll: true });
					searchFocused.current = true;
				}
			}
		};
		run();

		const observer = new MutationObserver(run);
		observer.observe(container, { childList: true, subtree: true });

		return () => observer.disconnect();
	}, []);

	return containerRef;
};

export default useBankListKeyboardAccessibility;
